import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { format, isValid } from "date-fns";
import { Loader } from "lucide-react";

import { BellIcon } from "@/components/icons";
import Badge, { BadgeProps } from "@/components/Badge";
import {
    useGetNotifications,
    useGetUnreadNotificationsCount,
    useMarkAllNotificationsAsRead,
    useMarkNotificationAsRead
} from "@/features/notifications/repositories/notifications-repository";
import { TNotification } from "@/types";

/** How many of the newest notifications the panel shows. */
const PAGE_SIZE = 8;

/**
 * Stage the API stamps on a complaint notification. `ComplaintService` reuses
 * `ApplicationType.LICENCE` for those, and puts the `CMP-…` complaint id in
 * `applicationId` — so the stage, not the type, decides where they open.
 */
const COMPLAINT_STAGE = "COMPLAINT";

// Decision codes the API can put on a notification: `LegalReviewStatus`,
// `FinalDecisionDTO.DecisionStatus`, `ComplaintStatus` and `LicenseStatus` all
// land in this one field.
const APPROVED_DECISIONS = [
    "APPROVED",
    "APPROVED_WITH_CONDITIONS_INSPECTION",
    "LEGAL_REVIEW_COMPLETED",
    "INSPECTION_COMPLETED",
    "FINAL_COMPLETED",
    "RESOLVED",
    "CLOSED"
];

const REJECTED_DECISIONS = [
    "REJECTED",
    "INSPECTION_REJECTED",
    "SUSPENDED",
    "EXPIRED"
];

function decisionIntent(decision: string): BadgeProps["intent"] {
    if (APPROVED_DECISIONS.includes(decision)) return "success";
    if (REJECTED_DECISIONS.includes(decision)) return "error";

    return "warning";
}

// `new Date(...)` on a malformed value yields an Invalid Date, which date-fns v4
// throws a RangeError on — so nothing reaches `format` unchecked.
function formatMoment(value: string | null | undefined) {
    if (!value) return "";

    const parsed = new Date(value);
    return isValid(parsed) ? format(parsed, "dd MMM, hh:mm a") : "";
}

/**
 * Detail route the notification points at, or null when it cannot be opened.
 * The API sends the numeric licence id for LICENCE, the `ZCT-…` application id
 * for INVESTMENT and the `CMP-…` complaint id for the complaint stage — each of
 * which is exactly what the matching detail route asks for.
 */
function routeFor(item: TNotification) {
    if (!item.applicationId) return null;

    if (item.stage === COMPLAINT_STAGE) {
        return `/complaints/${item.applicationId}`;
    }

    if (item.applicationType === "LICENCE") {
        return `/applications/licences/applications-details/${item.applicationId}`;
    }

    if (item.applicationType === "INVESTMENT") {
        return `/applications/investments/applications-details/${item.applicationId}`;
    }

    return null;
}

const NotificationsMenu = () => {
    const navigate = useNavigate();

    const { notifications, isLoading, isError } = useGetNotifications({
        pager: { page: 0, size: PAGE_SIZE }
    });
    const { unreadCount } = useGetUnreadNotificationsCount();
    const { mutate: markAsRead } = useMarkNotificationAsRead();
    const { mutate: markAllAsRead, isPending: isMarkingAll } = useMarkAllNotificationsAsRead();

    const items = notifications?.results ?? [];

    const onOpen = (item: TNotification, close: () => void) => {
        if (!item.read) markAsRead(item.id);

        const route = routeFor(item);

        if (route) {
            close();
            navigate(route);
        }
    };

    return (
        <Popover className="relative">
            <PopoverButton
                type="button"
                aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
                className="relative inline-flex justify-center items-center rounded-md border border-transparent p-1 text-secondary hover:bg-secondary-50 hover:border-secondary-100 focus:outline-none"
            >
                <BellIcon className="size-5" />

                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-red-600 text-[10px] leading-4 font-medium text-white">
                        {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                )}
            </PopoverButton>

            <PopoverPanel
                transition
                anchor="bottom end"
                className="z-20 w-[22rem] origin-top-right rounded-xl border border-zinc-100 bg-white shadow-lg transition duration-100 ease-out [--anchor-gap:var(--spacing-2)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
            >
                {({ close }) => (
                    <>
                        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-zinc-100">
                            <h5 className="text-sm font-bold text-secondary">
                                Notifications
                                {unreadCount > 0 && (
                                    <span className="text-primary"> ({unreadCount})</span>
                                )}
                            </h5>

                            {unreadCount > 0 && (
                                <button
                                    type="button"
                                    onClick={() => markAllAsRead()}
                                    disabled={isMarkingAll}
                                    className="text-xs text-primary underline disabled:opacity-50"
                                >
                                    Mark all as read
                                </button>
                            )}
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                            {isLoading ? (
                                <div className="flex justify-center items-center py-10 text-zinc-400">
                                    <Loader className="size-5 animate-spin" />
                                </div>
                            ) : isError ? (
                                <p className="px-4 py-8 text-sm text-center text-zinc-500">
                                    Could not load your notifications.
                                </p>
                            ) : items.length === 0 ? (
                                <p className="px-4 py-8 text-sm text-center text-zinc-500">
                                    You have no notifications yet.
                                </p>
                            ) : (
                                items.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => onOpen(item, close)}
                                        className="w-full text-left px-4 py-3 border-b border-zinc-50 last:border-b-0 hover:bg-zinc-50"
                                    >
                                        <div className="flex items-start gap-2">
                                            <span
                                                className={`mt-1.5 size-2 shrink-0 rounded-full ${item.read ? "bg-transparent" : "bg-primary"}`}
                                            />

                                            <div className="flex-1 space-y-1">
                                                <p className={`text-sm ${item.read ? "text-zinc-600" : "text-secondary font-medium"}`}>
                                                    {item.title}
                                                </p>

                                                {item.message && (
                                                    <p className="text-xs text-zinc-500">
                                                        {item.message}
                                                    </p>
                                                )}

                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-[11px] text-zinc-400">
                                                        {formatMoment(item.createdAt)}
                                                    </span>

                                                    {item.stage && (
                                                        <span className="text-[11px] text-zinc-400 capitalize">
                                                            {item.stage.replace(/_/g, " ").toLowerCase()}
                                                        </span>
                                                    )}

                                                    {item.decisionStatus && (
                                                        <Badge
                                                            intent={decisionIntent(item.decisionStatus.toUpperCase())}
                                                            text={item.decisionStatus.replace(/_/g, " ")}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </>
                )}
            </PopoverPanel>
        </Popover>
    );
};

export default NotificationsMenu;
